import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { SCHEDULE_POST_MODAL } from "../../../../constants/modal.constant";
import { useFacebookCore } from "../../../../cores/facebook";
import { useGlobalCore } from "../../../../cores/globals";
import { useInstagramCore } from "../../../../cores/instagram";
import { useLinkedInCore } from "../../../../cores/linkedIn";
import { useLinkedInBusinessCore } from "../../../../cores/linkedInBusiness";
import { useMeCore } from "../../../../cores/me";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { SocialProviderId } from "../../../../cores/socialPost/config";
import { SOCIAL_ACTION_TYPES } from "../../../../cores/socialPost/consts/socialActionType.constant";
import { useTwitterCore } from "../../../../cores/twitter";
import { useTiktokCore } from "../../../../cores/tiktok";
import { PostActionButtons } from "./PostActionButtons";
import "./ModernPostButtons.scss";

interface Props {
  from: string;
  repost: boolean;
  onReset: () => void;
}

export const ModernPostButtons = ({ from, repost, onReset }: Props) => {
  const intl = useIntl();
  const {
    socialPost: socialPostTwitter,
    socialPostVideo: socialPostVideoTwitter,
  } = useTwitterCore();
  const {
    socialPost: socialPostFacebook,
    socialPostVideo: socialPostVideoFacebook,
  } = useFacebookCore();
  const {
    socialPost: socialPostTiktok,
    socialPostVideo: socialPostVideoTiktok,
  } = useTiktokCore();
  const { socialPost: socialPostLinkedIn } = useLinkedInCore();
  const {
    socialPost: socialPostLinkedInBusiness,
    socialPostVideo: socialPostVideoLinkedInBusiness,
  } = useLinkedInBusinessCore();
  const {
    socialPost: socialPostInstagram,
    socialPostVideo: socialPostVideoInstagram,
    cleanErrors,
  } = useInstagramCore();
  const { company, companyPlan } = useMeCore();
  const { turnOnModal } = useGlobalCore();
  const {
    socialProviders,
    postErrors,
    images,
    video,
    text = "",
    mentions,
    setSocialAction,
    selectedPost,
  } = useSocialPostCore();

  const canSchedule = useMemo(
    () => companyPlan?.rules.allowScheduledPost,
    [companyPlan]
  );

  const disabled = useMemo(() => {
    const socialProviderIds = socialProviders.map((sp) => sp.id);
    const hasError =
      postErrors.find((error) =>
        socialProviderIds.includes(error.socialProviderId)
      ) != null;
    return (
      hasError ||
      (!text && !video && !images.length)
    );
  }, [postErrors, images.length, text, video]);

  const onSuccess = useCallback(() => {
    onReset();
    cleanErrors();
  }, [onReset, cleanErrors]);

  const onOpeningSchedule = useCallback(() => {
    if (!disabled && canSchedule) {
      turnOnModal(SCHEDULE_POST_MODAL, true);
    }
  }, [turnOnModal, disabled, canSchedule]);

  const post = useCallback(() => {
    const parseDate = "";
    setSocialAction(SOCIAL_ACTION_TYPES.POST);
    const repostId = repost ? selectedPost?.post.id! : "";
    
    socialProviders.forEach((sp) => {
      switch (sp.name) {
        case SocialProviderId.Twitter:
          video
            ? socialPostVideoTwitter(
                company?.id!,
                text,
                mentions,
                video!,
                parseDate,
                repostId,
                onSuccess
              )
            : socialPostTwitter(
                company?.id!,
                text,
                mentions,
                images,
                parseDate,
                repostId,
                onSuccess
              );
          break;
        case SocialProviderId.TikTok:
          video
            ? socialPostVideoTiktok(
                company?.id!,
                text,
                mentions,
                video!,
                parseDate,
                repostId,
                onSuccess
              )
            : socialPostTiktok(
                company?.id!,
                text,
                mentions,
                images,
                parseDate,
                repostId,
                onSuccess
              );
          break;
        case SocialProviderId.LinkedInBusiness:
          video
            ? socialPostVideoLinkedInBusiness(
                company?.id!,
                text,
                mentions,
                video!,
                parseDate,
                repostId,
                onSuccess
              )
            : socialPostLinkedInBusiness(
                company?.id!,
                text,
                mentions,
                images,
                parseDate,
                repostId,
                onSuccess
              );
          break;
        case SocialProviderId.LinkedIn:
          socialPostLinkedIn(
            company?.id!,
            text,
            mentions,
            images,
            parseDate,
            repostId,
            onSuccess
          );
          break;
        case SocialProviderId.Facebook:
          video
            ? socialPostVideoFacebook(
                company?.id!,
                text,
                mentions,
                video!,
                parseDate,
                repostId,
                onSuccess
              )
            : socialPostFacebook(
                company?.id!,
                text,
                mentions,
                images,
                parseDate,
                repostId,
                onSuccess
              );
          break;
        case SocialProviderId.Instagram:
          video
            ? socialPostVideoInstagram(
                company?.id!,
                text,
                mentions,
                video!,
                parseDate,
                repostId,
                onSuccess
              )
            : socialPostInstagram(
                company?.id!,
                text,
                mentions,
                images,
                parseDate,
                repostId,
                onSuccess
              );
          break;
        default:
          break;
      }
    });
  }, [
    socialProviders,
    video,
    mentions,
    company,
    text,
    repost,
    images,
    selectedPost?.post.id,
    socialPostVideoTwitter,
    socialPostTwitter,
    socialPostVideoLinkedInBusiness,
    socialPostLinkedInBusiness,
    socialPostLinkedIn,
    socialPostVideoFacebook,
    socialPostFacebook,
    socialPostVideoInstagram,
    socialPostInstagram,
    socialPostTiktok,
    socialPostVideoTiktok,
    onSuccess,
    setSocialAction,
  ]);

  const getSelectedProvidersText = () => {
    if (socialProviders.length === 0) return "";
    if (socialProviders.length === 1) return socialProviders[0].displayName;
    return intl.formatMessage({ id: "app.socialPostSelector.selectedNetworks" }, { count: socialProviders.length });
  };

  return (
    <div className="modern-post-buttons">
      {/* Selected providers info */}
      <div className="selected-providers-info">
        <span className="providers-label">{intl.formatMessage({ id: "app.socialPostSelector.willPublishOn" })}</span>
        <div className="providers-tags">
          {socialProviders.map((provider) => (
            <div key={provider.id} className="provider-tag">
              <span className="provider-name">{provider.displayName}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Action buttons */}
      <div className="action-buttons">
        {selectedPost && !repost ? (
          <PostActionButtons
            from={from}
            save={false}
            disabled={disabled}
            onReset={onReset}
          />
        ) : (
          <>
            <button
              className={`schedule-button ${!canSchedule ? 'disabled' : ''}`}
              disabled={disabled || !canSchedule}
              onClick={onOpeningSchedule}
            >
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>{intl.formatMessage({ id: "app.socialMediaPost.program" })}</span>
            </button>
            
            <button
              className="publish-button"
              disabled={disabled}
              onClick={post}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              <span>{intl.formatMessage({ id: "app.socialMediaPost.toPost" })}</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};
